// src/App.js
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Toaster } from 'react-hot-toast';

// Layouts
import MainLayout from './components/Layout/MainLayout';
import AdminLayout from './components/Layout/AdminLayout';

// Public Pages
import Home from './pages/Home';
import Fruits from './pages/Fruits';
import FruitDetail from './pages/FruitDetail';
import Login from './pages/Auth/Login';
import Register from './pages/Auth/Register';

// Buyer Pages
import BuyerDashboard from './pages/Buyer/Dashboard';
import BuyerEnquiries from './pages/Buyer/Enquiries';
import BuyerVerification from './pages/Buyer/Verification';
import BuyerPayment from './pages/Buyer/Payment';
import BuyerShipping from './pages/Buyer/Shipping';

// Admin Pages
import AdminDashboard from './pages/Admin/Dashboard';
import AdminFruits from './pages/Admin/Fruits';
import AdminAddFruit from './pages/Admin/AddFruit';
import AdminEnquiries from './pages/Admin/Enquiries';
import AdminPayments from './pages/Admin/Payments';
import AdminVerifications from './pages/Admin/Verifications';
import AdminShipments from './pages/Admin/Shipments';
import AdminUsers from './pages/Admin/Users';

// Auth Context
import { AuthProvider } from './context/AuthContext';

// Protected Route
const ProtectedRoute = ({ children, role }) => {
  const token = localStorage.getItem('token');
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  if (role && user.user_type !== role) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Public Route
const PublicRoute = ({ children }) => {
  const token = localStorage.getItem('token');

  if (token) {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    if (user.user_type === 'admin') {
      return <Navigate to="/admin/dashboard" replace />;
    }
    if (user.user_type === 'buyer') {
      return <Navigate to="/buyer/dashboard" replace />;
    }
  }

  return children;
};

// Theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#2e7d32',
      light: '#4caf50',
      dark: '#1b5e20',
    },
    secondary: {
      main: '#ff9800',
      light: '#ffb74d',
      dark: '#f57c00',
    },
    background: {
      default: '#f5f5f5',
      paper: '#ffffff',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontSize: '2.5rem',
      fontWeight: 700,
      '@media (min-width:600px)': {
        fontSize: '3.5rem',
      },
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 700,
      '@media (min-width:600px)': {
        fontSize: '2.5rem',
      },
    },
    h3: {
      fontSize: '1.75rem',
      fontWeight: 600,
      '@media (min-width:600px)': {
        fontSize: '2rem',
      },
    },
    h4: {
      fontSize: '1.5rem',
      fontWeight: 600,
    },
    h5: {
      fontSize: '1.25rem',
      fontWeight: 500,
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 500,
    },
  },
  components: {
    MuiContainer: {
      styleOverrides: {
        root: {
          paddingLeft: '16px',
          paddingRight: '16px',
          '@media (min-width:600px)': {
            paddingLeft: '24px',
            paddingRight: '24px',
          },
        },
      },
    },
  },
});

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<MainLayout />}>
              <Route index element={<Home />} />
              <Route path="fruits" element={<Fruits />} />
              <Route path="fruits/:id" element={<FruitDetail />} />
              <Route
                path="login"
                element={
                  <PublicRoute>
                    <Login />
                  </PublicRoute>
                }
              />
              <Route
                path="register"
                element={
                  <PublicRoute>
                    <Register />
                  </PublicRoute>
                }
              />
            </Route>

            {/* Buyer Routes */}
            <Route
              path="/buyer"
              element={
                <ProtectedRoute role="buyer">
                  <MainLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Navigate to="dashboard" replace />} />
              <Route path="dashboard" element={<BuyerDashboard />} />
              <Route path="enquiries" element={<BuyerEnquiries />} />
              <Route path="verification" element={<BuyerVerification />} />
              <Route path="payment/:enquiryId" element={<BuyerPayment />} />
              <Route path="shipping/:enquiryId" element={<BuyerShipping />} />
            </Route>

            {/* Admin Routes */}
            <Route
              path="/admin"
              element={
                <ProtectedRoute role="admin">
                  <AdminLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Navigate to="dashboard" replace />} />
              <Route path="dashboard" element={<AdminDashboard />} />
              <Route path="fruits" element={<AdminFruits />} />
              <Route path="fruits/add" element={<AdminAddFruit />} />
              <Route path="fruits/edit/:id" element={<AdminAddFruit />} />
              <Route path="enquiries" element={<AdminEnquiries />} />
              <Route path="payments" element={<AdminPayments />} />
              <Route path="verifications" element={<AdminVerifications />} />
              <Route path="shipments" element={<AdminShipments />} />
              <Route path="users" element={<AdminUsers />} />
            </Route>

            {/* Fallback */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Router>
      </AuthProvider>

      <Toaster 
        position="top-right"
        toastOptions={{
          duration: 3000,
          style: {
            background: '#363636',
            color: '#fff',
          },
          success: {
            style: {
              background: '#4caf50',
            },
          },
          error: {
            style: {
              background: '#f44336',
            },
          },
        }}
      />
    </ThemeProvider>
  );
}

export default App;